// External Dependencies
import {
  type KeyboardEvent, type MouseEvent, type ReactNode, useCallback, useState,
} from 'react';
import IconButton, { type IconButtonProps } from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu, { type MenuProps } from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import styled from 'styled-components';

// Local Typings
export interface MoreActionsItem<T> {
  action: ((row: T) => void) | (() => void);
  icon?: ReactNode;
  isDisabled?: boolean | ((row: T) => boolean);
  text: string;
}
interface Props<T> {
  disabled?: boolean;
  iconButtonProps?: IconButtonProps;
  moreActions: MoreActionsItem<T>[];
  row?: T;
}

// Local Variables
const StyledIconButton = styled(IconButton)({
  '.icon-button': {
    fontSize: '1.25rem',
    padding: 8,
  },
});

const anchorOrigin: MenuProps['anchorOrigin'] = { horizontal: 'right', vertical: 'bottom' };
const transformOrigin: MenuProps['transformOrigin'] = { horizontal: 'right', vertical: 'top' };

function handleKeyDown(event: KeyboardEvent) {
  if (['Enter', ' '].includes(event.key)) {
    event.stopPropagation();
  }
}

// Component Definition
function ToolbarMoreActionsIconMenu<T>({
  disabled,
  iconButtonProps,
  moreActions,
  row,
}: Props<T>) {
  const [anchorEl, setAnchorEl] = useState<MenuProps['anchorEl']>(null);
  const open = Boolean(anchorEl);

  const handleClick = useCallback((event: MouseEvent) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback((event: MouseEvent, action: MoreActionsItem<T>['action'] | null = null) => {
    event.stopPropagation();

    action?.(row as T);

    setAnchorEl(null);
  }, [row]);

  const getIsDisabled = useCallback((action: MoreActionsItem<T>): boolean => {
    if (typeof action.isDisabled !== 'function') {
      return Boolean(action.isDisabled);
    }

    if (row) {
      return action.isDisabled(row);
    }

    return false;
  }, [row]);

  return (
    <>
      <Tooltip
        key="icon-button-more-actions"
        placement="bottom-end"
        title="More Actions"
      >
        <div>
          <StyledIconButton
            aria-controls={open ? 'more-actions-menu' : undefined}
            aria-expanded={open ? 'true' : undefined}
            aria-haspopup="true"
            aria-label="More actions"
            aria-owns={open ? 'more-actions-menu' : undefined}
            classes={{
              sizeSmall: 'icon-button',
            }}
            disabled={disabled}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            size="small"
            {...iconButtonProps}
          >
            <MoreVertIcon fontSize="small" />
          </StyledIconButton>
        </div>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        anchorOrigin={anchorOrigin}
        id="more-actions-menu"
        onClose={(e) => handleClose(e as MouseEvent)}
        open={open}
        transformOrigin={transformOrigin}
      >
        {moreActions.map((action) => (
          <MenuItem
            disabled={getIsDisabled(action)}
            key={action.text}
            onClick={(e) => handleClose(e, action.action)}
            onKeyDown={handleKeyDown}
          >
            {action.icon && <ListItemIcon>{action.icon}</ListItemIcon>}
            {action.text && <ListItemText>{action.text}</ListItemText>}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

export default ToolbarMoreActionsIconMenu;
